import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { Switch, FormControlLabel } from '@mui/material'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { getCompanyUom } from '../../utils/companyUom'
import { LoadingSpinner } from '../../components/ui'
import { formatCurrency, formatQuantity } from '../../utils/formatters'
import { useLoadFormHistory, loadFormKeys } from '../../hooks/api/useLoadForms'
import { loadFormService } from '../../services/loadforms/loadFormService'
import { useToast } from '../../contexts/ToastContext'
import BillEditModal from '../../components/loadforms/BillEditModal'
import PaymentDetailsDialog from '../../components/loadforms/PaymentDetailsDialog'
import { Info as InfoIcon } from '@mui/icons-material'
import type { BillPaymentDetail, ChequeDetails } from '../../types/cheque'

interface SettlementData {
  cash_handed_over: number
  cash_collected: number
  sales_on_credit: number // Legacy field for backward compatibility
  credit_sales: CreditSale[] // New customer-specific credit sales
  daily_expense: number
  expense_remarks: string
  unsettled_amount: number
  unsettled_reason: string
  settled_at: string // ISO date string (YYYY-MM-DD); optional, defaults to today on the backend
}

interface CreditSale {
  customer_id: string
  customer_name: string
  amount: number
  bill_id: string
  remarks?: string
  cheque?: ChequeDetails
}

interface BillEditData {
  id: string
  customer_id: string
  customer_name: string
  customer_code?: string
  area: string
  quantity_cases: number
  discount: number
  adv_tax: number
  invoice_value: number
  sku_items: any[]
}

// Utility function to round monetary values to 2 decimal places
const roundMoney = (value: number): number => {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

const LoadFormSettlementPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const uom = getCompanyUom(user?.company_uom)
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  
  const [bills, setBills] = useState<BillEditData[]>([])
  const [cancelledBills, setCancelledBills] = useState<Set<string>>(new Set())
  const [billPaymentDetails, setBillPaymentDetails] = useState<Map<string, BillPaymentDetail>>(new Map())
  const [showPaymentDetailsDialog, setShowPaymentDetailsDialog] = useState(false)
  const [settlementData, setSettlementData] = useState<SettlementData>({
    cash_handed_over: 0,
    cash_collected: 0,
    sales_on_credit: 0,
    credit_sales: [],
    daily_expense: 0,
    expense_remarks: '',
    unsettled_amount: 0,
    unsettled_reason: '',
    settled_at: new Date().toISOString().split('T')[0]
  })
  const [isManualCashCollected, setIsManualCashCollected] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [showExpenseDialog, setShowExpenseDialog] = useState(false)
  const [expenseRemarks, setExpenseRemarks] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showBillEditModal, setShowBillEditModal] = useState(false)
  const [editingBill, setEditingBill] = useState<any>(null)
  const [showSettlementConfirmDialog, setShowSettlementConfirmDialog] = useState(false)

  // Use the actual API hook
  const { data: loadFormData, isLoading: isDataLoading, error, refetch } = useLoadFormHistory(id!)

  useEffect(() => {
    if (loadFormData?.loadForm) {
      const loadForm = loadFormData.loadForm
      
      // Transform bills data for the table
      const transformedBills = loadForm.bill_ids.map((bill: any) => ({
        id: bill._id,
        customer_id: typeof bill.customer_id === 'string' ? bill.customer_id : bill.customer_id?._id,
        customer_name: bill.customer_name,
        customer_code: typeof bill.customer_id === 'string' ? undefined : bill.customer_id?.customer_code,
        area: bill.area_id?.area_name || 'N/A',
        quantity_cases: bill.summary.quantity_ctn,
        discount: bill.discount,
        adv_tax: bill.adv_tax,
        invoice_value: bill.summary.net_payable,
        sku_items: bill.sku_items
      }))
      setBills(transformedBills)
      
      // Calculate total sales value
      const totalSalesValue = roundMoney(transformedBills.reduce((sum: number, bill: any) => sum + bill.invoice_value, 0))
      
      // Initialize payment details from settlement data or defaults
      const newPaymentDetails = new Map<string, BillPaymentDetail>()
      
      if (loadForm.settlement?.credit_sales && loadForm.settlement.credit_sales.length > 0) {
        // Build payment details map from existing settlement
        transformedBills.forEach((bill: BillEditData) => {
          const creditSale = loadForm.settlement.credit_sales.find((cs: any) => cs.bill_id === bill.id)
          if (creditSale) {
            // Bill has credit, calculate cash collected
            const creditGiven = creditSale.amount
            const cashCollected = roundMoney(bill.invoice_value - creditGiven)
            newPaymentDetails.set(bill.id, {
              cash_collected: cashCollected,
              credit_given: creditGiven,
              ...(creditSale.cheque ? { cheque: creditSale.cheque } : {}),
            })
          } else {
            // Bill is fully cash
            newPaymentDetails.set(bill.id, {
              cash_collected: bill.invoice_value,
              credit_given: 0
            })
          }
        })
      } else {
        // Default: all bills fully cash
        transformedBills.forEach((bill: BillEditData) => {
          newPaymentDetails.set(bill.id, {
            cash_collected: bill.invoice_value,
            credit_given: 0
          })
        })
      }
      
      setBillPaymentDetails(newPaymentDetails)

      // Set settlement data if available, otherwise use defaults
      if (loadForm.settlement) {
        // Calculate unsettled amount based on current state
        const totalCreditAmount = loadForm.settlement.credit_sales?.reduce((sum: number, credit: any) => sum + credit.amount, 0) || 0
        const currentCashCollected = loadForm.settlement.cash_collected || totalSalesValue
        const cashHandedOver = loadForm.settlement.cash_handed_over || 0
        const dailyExpense = loadForm.settlement.daily_expense || 0
        
        // Calculate unsettled: Cash Collected - Cash Handed Over - Daily Expense
        const calculatedUnsettled = roundMoney(Math.max(0, currentCashCollected - cashHandedOver - dailyExpense))
        
        setSettlementData({
          cash_handed_over: cashHandedOver,
          cash_collected: currentCashCollected,
          sales_on_credit: totalCreditAmount,
          credit_sales: loadForm.settlement.credit_sales || [],
          daily_expense: dailyExpense,
          expense_remarks: loadForm.settlement.expense_remarks || '',
          unsettled_amount: calculatedUnsettled,
          unsettled_reason: loadForm.settlement.unsettled_reason || '',
          settled_at: new Date().toISOString().split('T')[0]
        })
      } else {
        // Set default values - unsettled starts with total sales value (red styling)
        setSettlementData(prev => ({
          ...prev,
          cash_collected: totalSalesValue,
          unsettled_amount: totalSalesValue // Start with full amount as unsettled
        }))
      }
      
      // Mark initial load as complete
      setIsInitialLoad(false)
    }
  }, [loadFormData])

  const handleBillEdit = (billId: string) => {
    const bill = loadFormData?.loadForm?.bill_ids?.find((b: any) => b._id === billId)
    if (bill) {
      setEditingBill(bill)
      setShowBillEditModal(true)
    }
  }

  const handleBillSave = (updatedBill: any) => {
    // Update the bills state with the edited bill
    setBills(prev => prev.map(bill => 
      bill.id === updatedBill._id ? {
        ...bill,
        quantity_cases: updatedBill.summary.quantity_ctn,
        discount: updatedBill.discount,
        adv_tax: updatedBill.adv_tax,
        invoice_value: updatedBill.summary.net_payable,
        sku_items: updatedBill.sku_items
      } : bill
    ))
    
    // Refetch data to get updated totals
    refetch()
    setShowBillEditModal(false)
    setEditingBill(null)
  }


  const handleViewBill = (billId: string) => {
    window.open(`/bills/${billId}`, '_blank')
  }


  const handleSettlementChange = (field: keyof SettlementData, value: string | number) => {
    setSettlementData(prev => {
      const newData = {
        ...prev,
        [field]: value
      }
      
      // Track manual changes to cash_collected
      if (field === 'cash_collected') {
        setIsManualCashCollected(true)
        
        // Calculate unsettled amount: Cash Collected - Cash Handed Over - Daily Expense
        const unsettledAmount = roundMoney(Math.max(0, Number(value) - settlementData.cash_handed_over - settlementData.daily_expense))
        
        newData.unsettled_amount = unsettledAmount
      }
      
      // Handle cash handed over to distributor
      if (field === 'cash_handed_over') {
        // Recalculate unsettled amount when cash handed over changes
        const unsettledAmount = roundMoney(Math.max(0, settlementData.cash_collected - Number(value) - settlementData.daily_expense))
        newData.unsettled_amount = unsettledAmount
      }
      
      // Handle daily expense
      if (field === 'daily_expense') {
        // Recalculate unsettled amount when daily expense changes
        const unsettledAmount = roundMoney(Math.max(0, settlementData.cash_collected - settlementData.cash_handed_over - Number(value)))
        newData.unsettled_amount = unsettledAmount
      }
      
      return newData
    })
  }


  const handleBillToggle = (billId: string, isCancelled: boolean) => {
    setCancelledBills(prev => {
      const newSet = new Set(prev)
      if (isCancelled) {
        newSet.add(billId)
      } else {
        newSet.delete(billId)
      }
      return newSet
    })
  }

  // Update cash collected when cancelledBills or billPaymentDetails change
  useEffect(() => {
    // Skip update on initial load to prevent flash
    if (!isInitialLoad) {
      updateCashCollected()
    }
  }, [cancelledBills, billPaymentDetails, bills, isInitialLoad])


  const updateCashCollected = () => {
    // Calculate totals from billPaymentDetails
    let totalCashCollected = 0
    let totalCreditGiven = 0
    const creditSales: CreditSale[] = []
    
    bills.filter(bill => !cancelledBills.has(bill.id)).forEach(bill => {
      const payment = billPaymentDetails.get(bill.id)
      if (payment) {
        totalCashCollected += payment.cash_collected
        totalCreditGiven += payment.credit_given
        
        // Only include in credit_sales if credit > 0
        if (payment.credit_given > 0) {
          creditSales.push({
            customer_id: bill.customer_id,
            customer_name: bill.customer_name,
            amount: payment.credit_given,
            bill_id: bill.id,
            remarks: '',
            ...(payment.cheque ? { cheque: payment.cheque } : {}),
          })
        }
      } else {
        // Default: full cash if no payment details
        totalCashCollected += bill.invoice_value
      }
    })
    
    totalCashCollected = roundMoney(totalCashCollected)
    totalCreditGiven = roundMoney(totalCreditGiven)
    
    // Always recalculate cash_collected from payment details map
    // This ensures cancelled bills are excluded even if payment details were manually entered
    // The payment details map is the source of truth for active bills
    const unsettledAmount = roundMoney(Math.max(0, totalCashCollected - settlementData.cash_handed_over - settlementData.daily_expense))
    
    setSettlementData(prev => ({
      ...prev,
      cash_collected: totalCashCollected,
      sales_on_credit: totalCreditGiven,
      credit_sales: creditSales,
      unsettled_amount: unsettledAmount
    }))
    
    // Note: isManualCashCollected flag is used to show/hide the "Auto" button,
    // but we always recalculate from payment details when bills are cancelled/uncancelled
  }

  // Calculate total credit sales for display (excluding cancelled bills)
  const calculateTotalCreditSales = () => {
    return roundMoney(bills
      .filter(bill => !cancelledBills.has(bill.id))
      .reduce((sum, bill) => {
        const payment = billPaymentDetails.get(bill.id)
        return sum + (payment?.credit_given || 0)
      }, 0))
  }

  // Handle payment details dialog save
  const handlePaymentDetailsSave = (paymentDetails: Map<string, BillPaymentDetail>) => {
    setBillPaymentDetails(paymentDetails)
    setIsManualCashCollected(true) // Mark as manually set via dialog
    updateCashCollected()
  }

  const handleExpenseRemarks = () => {
    setShowExpenseDialog(true)
  }

  const handleSaveExpenseRemarks = () => {
    setSettlementData(prev => ({
      ...prev,
      expense_remarks: expenseRemarks
    }))
    setShowExpenseDialog(false)
  }


  const handleSettle = () => {
    // Show confirmation dialog instead of directly settling
    setShowSettlementConfirmDialog(true)
  }

  const confirmSettlement = () => {
    setShowSettlementConfirmDialog(false)
    setIsLoading(true)
    
    // Prepare credit_sales array from billPaymentDetails
    const creditSales: CreditSale[] = []
    let totalCashCollected = 0
    
    bills.filter(bill => !cancelledBills.has(bill.id)).forEach(bill => {
      const payment = billPaymentDetails.get(bill.id)
      if (payment) {
        totalCashCollected += payment.cash_collected
        // Only include in credit_sales if credit > 0
        if (payment.credit_given > 0) {
          creditSales.push({
            customer_id: bill.customer_id,
            customer_name: bill.customer_name,
            amount: payment.credit_given,
            bill_id: bill.id,
            remarks: '',
            ...(payment.cheque ? { cheque: payment.cheque } : {}),
          })
        }
      } else {
        // Default: full cash if no payment details
        totalCashCollected += bill.invoice_value
      }
    })
    
    // Prepare cancelled_bills array from cancelledBills state
    const cancelledBillsList = Array.from(cancelledBills)
    
    // Prepare settlement data with populated credit_sales and cancelled_bills
    const settlementPayload = {
      ...settlementData,
      cash_collected: roundMoney(totalCashCollected),
      credit_sales: creditSales,
      sales_on_credit: roundMoney(creditSales.reduce((sum, credit) => sum + credit.amount, 0)),
      cancelled_bills: cancelledBillsList
    }
    
    loadFormService.settleLoadForm(id!, settlementPayload)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['bills'] })
        queryClient.invalidateQueries({ queryKey: ['bill'] })
        queryClient.invalidateQueries({ queryKey: loadFormKeys.all })
        queryClient.invalidateQueries({ queryKey: ['account-ledger'] })
        queryClient.invalidateQueries({ queryKey: ['account-ledger-balance'] })
        showToast('Load form settled successfully', 'success')
        // Navigate back to load form details page after successful settlement
        navigate(`/loadforms/${id}`)
      })
      .catch((error) => {
        showToast(error.message, 'error')
        console.error('Error settling load form:', error)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleReset = () => {
    // Reset all settlement values to default state
    const { totalInvoiceValue } = calculateTotals()
    
    // Reset payment details to default (all cash)
    const defaultPaymentDetails = new Map<string, BillPaymentDetail>()
    bills.forEach(bill => {
      defaultPaymentDetails.set(bill.id, {
        cash_collected: bill.invoice_value,
        credit_given: 0
      })
    })
    setBillPaymentDetails(defaultPaymentDetails)
    
    setSettlementData({
      cash_handed_over: 0,
      cash_collected: totalInvoiceValue,
      sales_on_credit: 0,
      credit_sales: [],
      daily_expense: 0,
      expense_remarks: '',
      unsettled_amount: totalInvoiceValue,
      unsettled_reason: '',
      settled_at: new Date().toISOString().split('T')[0]
    })
    
    // Reset all toggles
    setCancelledBills(new Set())
    setIsManualCashCollected(false)
    
    showToast('Settlement values reset to default', 'info')
  }

  const calculateTotals = () => {
    const activeBills = bills.filter(bill => !cancelledBills.has(bill.id))
    const totalQuantity = activeBills.reduce((sum, bill) => sum + bill.quantity_cases, 0)
    const totalInvoiceValue = roundMoney(activeBills.reduce((sum, bill) => sum + bill.invoice_value, 0))
    return { totalQuantity, totalInvoiceValue }
  }


  const { totalQuantity, totalInvoiceValue } = calculateTotals()

  if (isDataLoading) {
    return <LoadingSpinner fullScreen message="Loading load form settlement..." />
  }

  if (error || !loadFormData) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {error ? 'Failed to load load form settlement data' : 'Load form not found'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/loadforms')}
          sx={{ mt: 2 }}
        >
          Back to Load Forms
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate(`/loadforms/${id}`)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">Load Form Settlement</Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleReset}
            disabled={isLoading}
            color="primary"
            sx={{ minWidth: 100 }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSettle}
            disabled={isLoading}
            sx={{ minWidth: 150 }}
          >
            Settle Load Form
          </Button>
        </Box>
      </Box>

      {/* Main Content - Side by Side Layout */}
      <Box display="flex" gap={3} sx={{ flexDirection: { xs: 'column', lg: 'row' } }}>
        {/* All Bills Table - Left Side */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper sx={{ mb: 3 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid #e8e4dd' }}>
              <Typography variant="h6" gutterBottom>
                All Bills
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Complete list of bills in this load form
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ py: 1, px: 1 }}>Customer</TableCell>
                    <TableCell sx={{ py: 1, px: 1 }}>Area</TableCell>
                    <TableCell sx={{ py: 1, px: 1 }}>Qty ({uom.code})</TableCell>
                    <TableCell sx={{ py: 1, px: 1 }}>Discount %</TableCell>
                    <TableCell sx={{ py: 1, px: 1 }}>Adv Tax %</TableCell>
                    <TableCell sx={{ py: 1, px: 1 }}>Amount</TableCell>
                    <TableCell sx={{ py: 1, px: 1 }}>Status</TableCell>
                    <TableCell sx={{ py: 1, px: 1 }}>Cancel Order</TableCell>
                    <TableCell sx={{ py: 1, px: 1 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bills.map((bill) => {
                    const isCancelled = cancelledBills.has(bill.id)
                    const payment = billPaymentDetails.get(bill.id)
                    const hasCredit = payment && payment.credit_given > 0
                    const isPartialPayment = payment && payment.cash_collected > 0 && payment.credit_given > 0
                    return (
                      <TableRow key={bill.id} sx={{
                        opacity: isCancelled ? 0.6 : 1,
                        bgcolor: isCancelled ? '#f7f5f2' : 'inherit'
                      }}>
                        <TableCell sx={{ py: 0.5, px: 1 }}>
                          <Typography variant="caption" fontWeight="medium">
                            {bill.customer_name}{bill.customer_code ? ` - ${bill.customer_code}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, px: 1 }}>
                          <Typography variant="caption">
                            {bill.area}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, px: 1 }}>
                          <Typography variant="caption">{formatQuantity(bill.quantity_cases)}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, px: 1 }}>
                          <Typography variant="caption">{formatQuantity(bill.discount)}%</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, px: 1 }}>
                          <Typography variant="caption">{formatQuantity(bill.adv_tax)}%</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, px: 1 }}>
                          <Typography variant="caption">Rs {formatCurrency(bill.invoice_value)}</Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5, px: 1 }}>
                          {isCancelled ? (
                            <span className="chip chip-danger">Cancelled</span>
                          ) : isPartialPayment ? (
                            <span className="chip chip-warn">Partial</span>
                          ) : hasCredit ? (
                            <span className="chip chip-warn">On Credit</span>
                          ) : (
                            <span className="chip chip-success">Paid</span>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 0.5, px: 1 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={isCancelled}
                                onChange={(e) => handleBillToggle(bill.id, e.target.checked)}
                                size="small"
                                sx={{ '& .MuiSwitch-thumb': { width: 16, height: 16 } }}
                              />
                            }
                            label=""
                            sx={{ margin: 0 }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 0.5, px: 1 }}>
                          <Box display="flex" gap={0.5}>
                            <Button
                              size="small"
                              startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                              onClick={() => handleViewBill(bill.id)}
                              variant="outlined"
                              sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.7rem' }}
                            >
                              View
                            </Button>
                            <Button
                              size="small"
                              startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                              onClick={() => handleBillEdit(bill.id)}
                              disabled={isCancelled}
                              sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.7rem' }}
                            >
                              Edit
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                  {/* Total Row */}
                  <TableRow sx={{ bgcolor: '#f7f5f2', fontWeight: 'bold' }}>
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      <Typography variant="caption" fontWeight="bold">
                        Total
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}></TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      <Typography variant="caption" fontWeight="bold">
                        {formatQuantity(totalQuantity)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}></TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}></TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      <Typography variant="caption" fontWeight="bold">
                        Rs {formatCurrency(totalInvoiceValue)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}></TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}></TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>

        {/* Settlement Form - Right Side */}
        <Box sx={{ width: { xs: '100%', lg: 320 }, flexShrink: 0 }}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem', mb: 1 }}>
              Settlement Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {/* Settlement Status Summary */}
            <Box sx={{ mb: 1, p: 1.5, bgcolor: settlementData.unsettled_amount === 0 ? 'success.50' : 'error.50', borderRadius: 1, border: 1, borderColor: settlementData.unsettled_amount === 0 ? 'success.main' : 'error.main' }}>
              <Typography variant="body2" sx={{ 
                color: settlementData.unsettled_amount === 0 ? 'success.main' : 'error.main',
                fontWeight: 'bold',
                textAlign: 'center',
              }}>
                {settlementData.unsettled_amount === 0 ? '✅ Settlement Complete' : `⚠️ Rs ${formatCurrency(settlementData.unsettled_amount)} Unsettled`}
              </Typography>
              {/* {settlementData.unsettled_amount > 0 && (
                <Typography variant="caption" sx={{ 
                  color: settlementData.unsettled_amount === 0 ? 'success.main' : 'error.main',
                  textAlign: 'center',
                  display: 'block'
                }}>
                  Add credit sales or cancel orders to reduce unsettled amount
                </Typography>
              )} */}
            </Box>
            
            <Box>
              <Card sx={{ mb: 2, bgcolor: '#f7f5f2' }}>
                <CardContent sx={{ py: 1, px: 2 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                    Total Sales Value
                  </Typography>
                  <Typography variant="h6" color="primary.main" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                    Rs {formatCurrency(totalInvoiceValue)}
                  </Typography>
                </CardContent>
              </Card>
              
              <TextField
                fullWidth
                label="Cash Collected from market"
                type="number"
                value={settlementData.cash_collected}
                onChange={(e) => handleSettlementChange('cash_collected', Number(e.target.value))}
                size="small"
                InputProps={{
                  endAdornment: (
                    <Box display="flex" gap={0.5}>
                      {isManualCashCollected && (
                        <Button 
                          size="small" 
                          variant="text" 
                          onClick={() => {
                            setIsManualCashCollected(false)
                            updateCashCollected()
                          }} 
                          sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                        >
                          Auto
                        </Button>
                      )}
                      <Button 
                        size="small" 
                        variant="text" 
                        startIcon={<InfoIcon sx={{ fontSize: 14 }} />}
                        onClick={() => setShowPaymentDetailsDialog(true)}
                        sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}
                      >
                        Enter Details
                      </Button>
                    </Box>
                  )
                }}
                sx={{ mb: 1.5,
                  '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                  '& .MuiInputBase-input': { fontSize: '0.8rem', py: 1 },
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
                inputProps={{
                  style: { 
                    MozAppearance: 'textfield',
                    WebkitAppearance: 'none',
                    margin: 0
                  }
                }}
              />
              
              <TextField
                fullWidth
                label="Sales on Credit"
                type="number"
                value={calculateTotalCreditSales()}
                size="small"
                disabled
                sx={{ mb: 1.5,
                  '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                  '& .MuiInputBase-input': { fontSize: '0.8rem', py: 1 },
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
                inputProps={{
                  style: { 
                    MozAppearance: 'textfield',
                    WebkitAppearance: 'none',
                    margin: 0
                  }
                }}
              />
              
              
              <TextField
                fullWidth
                label="Daily Expense"
                type="number"
                value={settlementData.daily_expense}
                onChange={(e) => handleSettlementChange('daily_expense', Number(e.target.value))}
                size="small"
                InputProps={{
                  endAdornment: (
                    <Button size="small" variant="text" onClick={handleExpenseRemarks} sx={{ fontSize: '0.7rem', minWidth: 'auto', px: 1 }}>
                      Enter Remarks
                    </Button>
                  )
                }}
                sx={{ mb: 1.5,
                  '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                  '& .MuiInputBase-input': { fontSize: '0.8rem', py: 1 },
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
                inputProps={{
                  style: { 
                    MozAppearance: 'textfield',
                    WebkitAppearance: 'none',
                    margin: 0
                  }
                }}
              />

              <TextField
                fullWidth
                label="Cash Handed Over to Distributor"
                type="number"
                value={settlementData.cash_handed_over}
                onChange={(e) => handleSettlementChange('cash_handed_over', Number(e.target.value))}
                size="small"
                sx={{ mb: 1.5, 
                  '& .MuiInputLabel-root': { fontSize: '0.75rem' },
                  '& .MuiInputBase-input': { fontSize: '0.8rem', py: 1 },
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
                inputProps={{
                  style: { 
                    MozAppearance: 'textfield',
                    WebkitAppearance: 'none',
                    margin: 0
                  }
                }}
              />
              
              <TextField
                fullWidth
                label={settlementData.unsettled_amount === 0 ? "✅ Fully Settled" : "⚠️ Unsettled Amount"}
                type="number"
                value={settlementData.unsettled_amount}
                size="small"
                disabled
                sx={{
                  '& .MuiInputLabel-root': { 
                    fontSize: '0.75rem',
                    color: settlementData.unsettled_amount !== 0 ? 'error.main' : 'success.main',
                    fontWeight: 'bold'
                  },
                  '& .MuiInputBase-input': { 
                    fontSize: '0.8rem', 
                    py: 1,
                    color: settlementData.unsettled_amount !== 0 ? 'error.main' : 'success.main',
                    fontWeight: 'bold'
                  },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: settlementData.unsettled_amount !== 0 ? 'error.50' : 'success.50',
                    '& fieldset': {
                      borderColor: settlementData.unsettled_amount !== 0 ? 'error.main' : 'success.main',
                      borderWidth: 2
                    },
                    '&:hover fieldset': {
                      borderColor: settlementData.unsettled_amount !== 0 ? 'error.dark' : 'success.dark'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: settlementData.unsettled_amount !== 0 ? 'error.main' : 'success.main'
                    }
                  },
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                }}
                inputProps={{
                  style: { 
                    MozAppearance: 'textfield',
                    WebkitAppearance: 'none',
                    margin: 0
                  }
                }}
              />
            </Box>
          </Paper>
        </Box>
      </Box>




      {/* Expense Remarks Dialog */}
      <Dialog open={showExpenseDialog} onClose={() => setShowExpenseDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enter Expense Remarks</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={expenseRemarks}
            onChange={(e) => setExpenseRemarks(e.target.value)}
            placeholder="Enter expense remarks..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExpenseDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveExpenseRemarks} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Bill Edit Modal */}
      {editingBill && (
        <BillEditModal
          open={showBillEditModal}
          onClose={() => {
            setShowBillEditModal(false)
            setEditingBill(null)
          }}
          billData={editingBill}
          onSave={handleBillSave}
        />
      )}

      {/* Payment Details Dialog */}
      <PaymentDetailsDialog
        open={showPaymentDetailsDialog}
        onClose={() => setShowPaymentDetailsDialog(false)}
        onSave={handlePaymentDetailsSave}
        bills={bills.filter(bill => !cancelledBills.has(bill.id)).map(bill => ({
          id: bill.id,
          customer_name: bill.customer_name,
          invoice_value: bill.invoice_value
        }))}
        initialPaymentDetails={billPaymentDetails}
      />

      {/* Settlement Confirmation Dialog */}
      <Dialog
        open={showSettlementConfirmDialog}
        onClose={() => setShowSettlementConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            <Typography variant="h6">Confirm Settlement</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to settle this load form?
          </Typography>
          <TextField
            fullWidth
            label="Settlement Date"
            type="date"
            size="small"
            value={settlementData.settled_at}
            onChange={(e) => setSettlementData(prev => ({ ...prev, settled_at: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: new Date().toISOString().split('T')[0] }}
            sx={{ mb: 2 }}
          />
          <Alert severity="warning">
            This action cannot be undone. Once settled, the load form status will be changed to closed and all bills will be marked as serviced.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setShowSettlementConfirmDialog(false)} 
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmSettlement} 
            variant="contained" 
            color="warning"
            startIcon={<SaveIcon />}
          >
            Yes, Settle Load Form
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Overlay */}
      {isLoading && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bgcolor="rgba(0,0,0,0.5)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={9999}
        >
          <CircularProgress color="primary" />
        </Box>
      )}
    </Box>
  )
}

export default LoadFormSettlementPage
